import { API, getToken } from "@/lib/api";

/**
 * Reminder ringtones. Built-ins are synthesized wav files shipped in
 * /public/sounds; custom ones live in the user's library on the backend and
 * are referenced as "custom:<sound_id>".
 */
export const BUILTIN_SOUNDS = [
  { id: "chime", label: "Chime", hint: "Soft two-tone" },
  { id: "bell", label: "Bell", hint: "Classic" },
  { id: "coin", label: "Arcade coin", hint: "Retro" },
  { id: "boing", label: "Boing", hint: "Funny" },
  { id: "whistle", label: "Slide whistle", hint: "Funny" },
  { id: "trombone", label: "Sad trombone", hint: "Funny" },
  { id: "laser", label: "Laser", hint: "Zap" },
  { id: "airhorn", label: "Airhorn", hint: "Loud" },
];

export const DEFAULT_SOUND = "chime";

export const isCustom = (sound) => typeof sound === "string" && sound.startsWith("custom:");
export const customId = (sound) => (isCustom(sound) ? sound.slice("custom:".length) : null);

export function soundSrc(sound) {
  if (!sound || sound === "none") return null;
  if (isCustom(sound)) {
    return `${API}/reminder-sounds/${customId(sound)}/audio?auth=${encodeURIComponent(getToken() || "")}`;
  }
  const known = BUILTIN_SOUNDS.some((s) => s.id === sound);
  return `${process.env.PUBLIC_URL || ""}/sounds/${known ? sound : DEFAULT_SOUND}.wav`;
}

let current = null;

/** Play a ringtone once, stopping whatever was playing before. */
export function playSound(sound) {
  const src = soundSrc(sound);
  if (!src) return null;
  try {
    if (current) { current.pause(); current.currentTime = 0; }
    const audio = new Audio(src);
    audio.volume = 0.9;
    current = audio;
    audio.play().catch(() => { /* blocked until the user interacts — fine */ });
    return audio;
  } catch {
    return null;
  }
}

export function stopSound() {
  try { current?.pause(); } catch { /* ignore */ }
  current = null;
}

export function soundLabel(sound, library = []) {
  if (!sound || sound === "none") return "Silent";
  if (isCustom(sound)) {
    const id = customId(sound);
    return library.find((s) => s.sound_id === id)?.name || "My sound";
  }
  return BUILTIN_SOUNDS.find((s) => s.id === sound)?.label || "Chime";
}
